import React, { useState, useEffect, useRef } from 'react'
import { Box, Image, Input, Textarea, Select, useToast, Button, 
	Modal, ModalOverlay, ModalCloseButton, ModalContent, ModalHeader, ModalBody} from '@chakra-ui/react'
import PreviewItem from '../PreviewItem'
import { useAuth } from "../../contexts/AuthContext";
import { Web3Storage } from 'web3.storage';
import { BASE_API_URL, WEB3STORAGE_TOKEN, NFT_ADDRESS, MAX_COVER_IMAGE_SIZE, MAX_TRACK_FILE_SIZE, MAIN_COLOR } from '../../config';
import ConfirmationProgress from '../ConfirmationProgress';
import intl from '../../utils/intl';
import { ethers } from 'ethers';
import nft from "../../abi/nft.json";
import { checkJwToken } from '../../services/walletLogin';
import { IoMdImages } from 'react-icons/io';
import { SiApplemusic } from 'react-icons/si';

const imageMimeType = /image\/(png|jpg|jpeg|gif)/i;
const trackMimeType = /audio\/(mpeg|mp3)/i;

const CreateItemForm = () => {

  function getAccessToken () {
    return WEB3STORAGE_TOKEN;
  }

  function makeStorageClient () {
    return new Web3Storage({ token: getAccessToken() })
  }

  const { currentAccount, currentNetwork, jwToken } = useAuth();

  const toast = useToast();

  const [ title, setTitle ] = useState()
  const [ artist, setArtist ] = useState()
	const [ IPOwner, setIPOwner ] = useState()
	const [ IPIncentive, setIPIncentive ] = useState()
  const [ selectValue, setSelectValue ] = useState(0)
  const [ categoryValue, setCategoryValue ] = useState(26)
	const [ hiddenConfirmationProgress, setHiddenConfirmationProgress ] = useState(true);
	const [ confirmationProgressData, setConfirmationProgressData ] = useState({value: 5, message: intl.get("start"), step: 1});
  const [ isLoading, setIsLoading ] = useState(false)
	const [ coverSize, setCoverSize ] = useState({w: 0, h: 0});

  const descriptionRef = useRef()
  const royaltiesRef = useRef(0)

  const [ file, setFile ] = useState(null)
  const [ imageURL, setImageURL ] = useState()

  const [ trackFile, setTrackFile ] = useState(null)
  const [ trackURL, setTrackURL ] = useState()

  const [ categoryArr, setCategoryArr ] = useState([])

  const handleImageUpload = (e) => {

    const file = e.target.files;
    if (!file[0].type.match(imageMimeType)) {
			showError(intl.get('invalid_file_type'));
      return;
    }

    if (!file[0].size / 1024 > MAX_COVER_IMAGE_SIZE) {
			showError(intl.get("image_size_too_big") + (MAX_COVER_IMAGE_SIZE / 1024).toFixed(0) + "M");
      return;
    }

    setFile(file[0])
  }

	const showError = (msg) => {
		toast({
			title: intl.get("error"),
			description: msg,
			status: "error",
			duration: 4000,
			isClosable: true
		})
	}
  const handleTrackUpload = (e) => {

    const file = e.target.files;

    if (!file[0].type.match(trackMimeType)) {
			showError(intl.get("invalid_file_type"));
      return;
    }

    if (!file[0].size / 1024 > MAX_TRACK_FILE_SIZE) {
			showError(intl.get("track_size_too_big") + (MAX_TRACK_FILE_SIZE / 1024).toFixed(0) + "M");
      return;
    }
    setTrackFile(file[0])
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if(!currentAccount) {
			showError(intl.get("please_log_in"));
      return;
    }

		if(jwToken === undefined) {
			showError(intl.get("please_log_in"));
			return;
		}

		const res = await checkJwToken(jwToken, currentAccount);
		if(!res) {
			showError(intl.get("please_log_in"));
			return;
		}
		
    if(!file || !trackFile) {
      let _description = file ? intl.get("upload_track") : intl.get("upload_cover_image")
			showError(_description);
      return;
    }

		if(coverSize.w / coverSize.h !== 1 || coverSize.w < 420) {
			showError(intl.get("cover_image_ratio_wrong"));
			return;
		}
    if(!title || !artist) {
      let _description = title ? intl.get("upload_artist_name") : intl.get("upload_title") 
			showError(_description); 
      return;
    }

		if(!IPOwner || !IPIncentive) {
			let _description = IPOwner ? intl.get("upload_ip_incentive_address") : intl.get("upload_ip_address");
			showError(_description);
			return;
		}

		try {
			setIPOwner(ethers.utils.getAddress(IPOwner));
			setIPIncentive(ethers.utils.getAddress(IPIncentive));
		} catch (err) {
			let _description = intl.get("checksum_error");
			showError(_description);
			return;
		}

    if(!descriptionRef.current.value || !categoryValue) {
      let _description = categoryValue ? intl.get("upload_description") : intl.get("select_category")
			showError(_description); 
      return;
    }
    
    let _imageURL, _trackURL;

    try {
      setIsLoading(true);

			const _fileArr = [
        file,
        trackFile
      ]
      
      const { ethereum } = window; //injected by metamask
      //connect to an ethereum node
      const provider = new ethers.providers.Web3Provider(ethereum); 
      //gets the account
      const signer = provider.getSigner(); 
      //connects with the contract
      const NFTContract = new ethers.Contract(NFT_ADDRESS, nft, signer);

			//Check if has mint right
			const isMintWhitelist = await NFTContract.isMintWhitelist(currentAccount);
			if(!isMintWhitelist) {
				toast({
					title: intl.get("error"),
					description: intl.get("no_right_to_mint"),
					status: "error",
					duration: 4000,
					isClosable: true
				})
				return;
			}

			// Step 1: upload cover picture and track audio file to ipfs
      const client = makeStorageClient();
			setHiddenConfirmationProgress(false);
			setConfirmationProgressData({step: '1/5', value: 25, message: intl.get("mint_step_1")});

      const _cid = await client.put(_fileArr);
      _imageURL = "ipfs://"+_cid+"/"+file.name.replace(/\s/g, '%20')
      _trackURL = "ipfs://"+_cid+"/"+trackFile.name.replace(/\s/g, '%20')

      toast({
        title: intl.get("success"),
        description: intl.get("image_mp3_uploaded"),
        status: "success",
        duration: 4000,
        isClosable: true
      })

			// Step 2: upload metadata to ipfs
			setConfirmationProgressData({step: '2/5', value: 50, message: intl.get("mint_step_2")});
      const metadata = {
        "name": title,
        "description": descriptionRef.current.value,
        "attributes": {
          "artist": artist,
					"IPOwner": IPOwner,
					"IPIncentive": IPIncentive,
          "collection": selectValue,
          "category": categoryValue,
          },
        "royality": 0,
        "image": _imageURL,
        "audio": _trackURL
      }

      const blob = new Blob([JSON.stringify(metadata)], { type: 'application/json' })
      const _metadataArr = [
        new File([blob], 'metadata.json')
      ]
      const _metadataCID = await client.put(_metadataArr);
      console.log("ipfs://"+_metadataCID+"/metadata.json")

      if(_metadataCID) {
        toast({
          title: intl.get("success"),
          description: intl.get("metadata_uploaded"),
          status: "success",
          duration: 4000,
          isClosable: true
        })
      }
      
			// Step 3: mint NFT on chain
			setConfirmationProgressData({step: '3/5', value: 75, message: intl.get("mint_step_3")});

			const mintTxn = await NFTContract.mint(
				currentAccount, 
				"ipfs://"+_metadataCID+"/metadata.json", 
				royaltiesRef.current.value === undefined ? 0 : royaltiesRef.current.value,
				IPOwner,
				IPIncentive
			);
      const res = await mintTxn.wait();
      const tokenId = parseInt(res["events"][0]["args"][2]);
      
			// Step 4: insert a new item data to database
			// ###### monitor也会更新数据，如何兼容？
			setConfirmationProgressData({step: '4/5', value: 90, message: intl.get("mint_step_4")});

      fetch(`${BASE_API_URL}/items/add_items/`, {
        method: "post",
				headers: {
					'Authorization': jwToken,
					'Content-Type': 'application/json'
				},	
        body: JSON.stringify(
          {
            "chainId": currentNetwork,
            "nftAddress": NFT_ADDRESS,
            "tokenId": tokenId,
            "tokenURI": "ipfs://"+_metadataCID+"/metadata.json",
            "owner": currentAccount,
            "collectionId": selectValue,
            "categoryId": categoryValue,
						"name": title,
						"artist": artist,
						"description": descriptionRef.current.value,
          }
        )
      }).then(res => res.json()).then( data => console.log(data) )

			// Mint nft over
      toast({
        title: intl.get("success"),
        description: intl.get('nft_mint_success'),
        status: "success",
        duration: 4000,
        isClosable: true
      })

			setConfirmationProgressData({step: '5/5', value: 100, message: intl.get("mint_step_5")});
			setTimeout(() => {
				setHiddenConfirmationProgress(true);
			}, 3000)
			

    } catch(err) {
      console.log(err.message);
      toast({
        title: intl.get("error"),
        description: intl.get("ipfs_upload_error"),
        status: "error",
        duration: 4000,
        isClosable: true
      })
			setHiddenConfirmationProgress(true);

    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    let fileReader, isCancel = false;
    if (file) {
      fileReader = new FileReader();
      fileReader.onload = (e) => {
        const { result } = e.target;
        if (result && !isCancel) {
          setImageURL(result)
        }
      }
      fileReader.readAsDataURL(file);
    }
  
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    }
  }, [file])
  
  useEffect(() => {
    let fileReader, isCancel = false;
    if (trackFile) {
      fileReader = new FileReader();
      fileReader.onload = (e) => {
        const { result } = e.target;
        if (result && !isCancel) {
          setTrackURL(result)
        }
      }
      fileReader.readAsDataURL(trackFile);
    }
  
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    }
  }, [trackFile])

  useEffect(() => {
    let _resArr = [];
    let fetchRes = fetch(`${BASE_API_URL}/items/get_categories/`);
    fetchRes.then(res => res.json()).then(
      data => {
        _resArr = _resArr.concat((data["result"]))
        if(_resArr.length!==0 && categoryArr.length===0) {
          setCategoryArr((Array.from({length: _resArr.length}, (_, i) => i).map((number, index)=>
          <option key={index} value={_resArr[index][0]}>{_resArr[index][1]}</option>
        )))
        }
        // console.log(_resArr)
    }).catch(err => console.log("Failed to fetch categories."))
    
  }, [categoryArr])

  return (
    <Box>
      <div className="create-items mt-3 pt-3 mt-md-4 pt-md-4 mt-lg-5 pt-lg-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-7 create-form-outer">
              <div className="title-heading mb-4 pb-2 pb-lg-0 ">
                <h2 className="headingWh ">{intl.get("create_item")}</h2>
                <p>{intl.get("best_must_nft")}</p>
              </div>
              <div className="create-form">
                <form>
                  <div className="row">
                    <div className="col-lg-12 pb-md-4 pb-3 ">
                      <label>{intl.get("upload_cover_image")}</label>
                      <div className="img-choosen">
											<Input id="upload-cover" className="file-upload-input" type="file" accept=".jpg, .png, .gif, .jpeg" onChange={handleImageUpload}/>
                        <div className="drag-text">
													<label for="upload-cover">
														<IoMdImages size="65" style={{color: MAIN_COLOR}}/>
	                          {/* <Image src={file ? "/assets/img/checkmark.svg" : "/assets/img/single.svg"} className="img-fluid margin-auto" alt="choose image" /> */}
														<label>{!file ? intl.get("reupload") : ""}</label>
													</label>
                        </div>
                      </div>
											
                    </div>
                    <div className="col-lg-12 pb-md-4 pb-3">
                      <label>{intl.get("upload_track")}</label>
												<div className="img-choosen">
													<Input id="upload-track" className="file-upload-input" type="file" accept=".mp3" onChange={handleTrackUpload}/>
													<div className="drag-text">
														<label for="upload-track">
															<SiApplemusic size="60" style={{color: MAIN_COLOR, marginBottom: "5px"}}/>
															{/* <Image src={trackFile ? "/assets/img/checkmark.svg" : "/assets/img/upload-icon.svg"} className="img-fluid margin-auto" alt="upload icon" /> */}
															<label>{!trackFile ? intl.get("reupload") : ""}</label>
														</label>
													</div>
												</div>
                    </div>
                    {/* <div className="col-md-7 col-6 pb-md-4 pb-3">
                      <label>Price</label>
                      <Input type="number" placeholder="Price" className="form-control" onChange={e => setPrice(e.target.value) }/>
                    </div> */}
                    <div className="col-md-7 pb-md-4 pb-3">
                      <label>{intl.get("select_category")}</label>
                      <div className="btn-group w-100">
                        {categoryArr && 
                        <Select className="input-bd" placeholder='New Category' value={categoryValue} onChange={e => setCategoryValue(e.target.value)}>
                          {categoryArr}
                        </Select>
                        }
                      </div>
                    </div>
                    <div className="col-md-7 pb-md-4 pb-3">
                      <label>{intl.get("select_collection")}</label>
                      <div className="btn-group w-100">
                        <Select className="input-bd" placeholder='New Collection' value={selectValue} onChange={e => setSelectValue(e.target.value)}>
                          <option value='1'>Nusic</option>
                        </Select>
                      </div>
                    </div>
                    <div className="col-xl-9 pb-md-4 pb-3">
                      <label>{intl.get("upload_title")}</label>
                      <Input type="text" placeholder="Enter Name" className="form-control" onChange={e => setTitle(e.target.value) }/>
                    </div>
                    <div className="col-xl-9 pb-md-4 pb-3">
                      <label>{intl.get("upload_artist_name")}</label>
                      <Input type="text" placeholder="Enter Name" className="form-control" onChange={e => setArtist(e.target.value) }/>
                    </div>
                    <div className="col-xl-9 pb-md-4 pb-3">
                      <label>{intl.get("upload_description")}</label>
                      <Textarea className="form-control" placeholder="Enter the description" defaultValue={""} ref={descriptionRef} />
                    </div>
                    {/* <div className="col-md-7 col-6 pb-md-4 pb-3">
                      <label>{intl.get("royality")}</label>
                      <input type="number" placeholder="0, 10, 20 or more" className="form-control" ref={royaltiesRef}/>
                    </div> */}
                    <div className="col-xl-9 pb-md-4 pb-3">
                      <label>{intl.get("upload_ip_address")}</label>
                      <Input type="text" placeholder="Enter IP owner address" className="form-control" onChange={e => setIPOwner(e.target.value) }/>
                    </div>
                    <div className="col-xl-9 pb-md-4 pb-3">
                      <label>{intl.get("upload_ip_incentive_address")}</label>
                      <Input type="text" placeholder="Enter incentive address" className="form-control" onChange={e => setIPIncentive(e.target.value) }/>
                    </div>
                    <div className="col-lg-12 d-none d-lg-block ">
                      <Button style={{ backgroundColor: MAIN_COLOR, color: "white" }} onClick={handleSubmit} isLoading={isLoading}>{intl.get("start_create")}</Button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <PreviewItem onImageLoaded={(size) => setCoverSize(size)} imageSRC={imageURL} trackSRC={trackURL}/>
          </div>
        </div>
      </div>

			<Modal 
        isCentered
        isOpen={!hiddenConfirmationProgress} 
        closeOnOverlayClick={false}
        // onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mint Nusic NFT</ModalHeader>
          {/* <ModalCloseButton/> */}
          <ModalBody>
            <ConfirmationProgress 
              step={confirmationProgressData.step}
              value={confirmationProgressData.value}
              message={confirmationProgressData.message}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default CreateItemForm;